import { REACT_APP_API_URL } from 'helpers/conf';
import { API_PATH } from 'helpers/constants';
import qs from 'qs';
import { delay } from '../helpers';
import { ContentTypes, getResponseCamelization } from '../helpers/api';
import type { RequestConfig, RequestContext, RequestFn } from '../types/api';
import type { RequestMethod } from '../types/common';

export type { RequestConfig } from '../types/api';

export type ApiResponse<T> = {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
};

const BASE_URL = `${REACT_APP_API_URL}${API_PATH}`.replace(/\/?$/, '/');

const _parsedTimeout = Number.parseInt(
  process.env.REACT_APP_REQUEST_TIMEOUT || '20',
  10
);
const TIMEOUT_MS = (Number.isNaN(_parsedTimeout) ? 20 : _parsedTimeout) * 1000;

// 500 was here before but removed — it prevented retries and no callers depend on it being resolved
const errorStatuses = new Set([400, 404]);

const defaultTransform = [getResponseCamelization()];

const STATIC_HEADERS: Record<string, string> = {
  accept: ContentTypes.vndJson,
  'content-type': ContentTypes.json,
};

function combineSignals(...signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();
  for (const sig of signals) {
    if (sig.aborted) {
      controller.abort();
      break;
    }
    sig.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller.signal;
}

function hasErrors(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const errors = (data as Record<string, unknown>).errors;
  return Array.isArray(errors)
    ? errors.length > 0
    : errors !== null && errors !== undefined;
}

function resolveUrl(uri: string): string {
  if (uri.startsWith('http://') || uri.startsWith('https://')) return uri;
  return `${BASE_URL}${uri.replace(/^\/+/, '')}`;
}

function buildRequest(
  method: RequestMethod,
  uri: string,
  data: object | undefined,
  headers: Record<string, string>
): { url: string; init: RequestInit } {
  let url = resolveUrl(uri);
  const init: RequestInit = {
    method: method.toUpperCase(),
    headers,
    credentials: 'include',
  };

  if (method === 'get') {
    if (data !== undefined) {
      url += `${url.includes('?') ? '&' : '?'}${qs.stringify(data, { arrayFormat: 'repeat' })}`;
    }
  } else if (method !== 'head' && data !== undefined) {
    init.body = data instanceof FormData ? data : JSON.stringify(data);
  }

  return { url, init };
}

async function executeWithRetry<T>(
  url: string,
  init: RequestInit,
  transforms: Array<
    (data: unknown, headers?: Record<string, string>) => unknown
  >,
  callerSignal: AbortSignal | undefined,
  retryCount: number,
  retryDelay: number,
  attempt: number
): Promise<ApiResponse<T>> {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), TIMEOUT_MS);

  const signal =
    callerSignal !== undefined
      ? combineSignals(callerSignal, timeoutController.signal)
      : timeoutController.signal;

  try {
    const response = await fetch(url, { ...init, signal });
    clearTimeout(timeoutId);

    const headersMap: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headersMap[key] = value;
    });

    const text = response.status === 204 ? '' : await response.text();
    let parsed: unknown = text === '' ? null : text;
    if (parsed !== null) {
      for (const transform of transforms) {
        parsed = transform(parsed, headersMap);
      }
    }

    const apiResponse: ApiResponse<T> = {
      data: parsed as T,
      status: response.status,
      statusText: response.statusText,
      headers: headersMap,
    };

    // 400/404 are expected errors — resolve instead of reject so callers can handle them inline without try/catch
    if (errorStatuses.has(response.status)) return apiResponse;
    if (hasErrors(parsed)) return Promise.reject(apiResponse);

    if (!response.ok) {
      if (attempt < retryCount && response.status >= 500) {
        console.warn(
          `Request failed with status ${response.status}. Retrying ${attempt + 1}/${retryCount}...`
        );
        const backoff = retryDelay * 2 ** attempt + Math.random() * 1000;
        await delay(backoff);
        return executeWithRetry<T>(
          url,
          init,
          transforms,
          callerSignal,
          retryCount,
          retryDelay,
          attempt + 1
        );
      }
      return Promise.reject(apiResponse);
    }

    return apiResponse;
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    throw error;
  }
}

export function request<T = unknown>(
  // biome-ignore lint/suspicious/noConfusingVoidType: void is required — TypeScript maps "called without this" to void, not undefined
  this: RequestConfig | void,
  method: RequestMethod,
  uri: string,
  data?: object,
  functionScopedConfig?: RequestConfig,
  mockData?: { data: T; timeout: number },
  retryCount = 3,
  retryDelay = 2000
): Promise<ApiResponse<T>> {
  const contextConfig: RequestConfig =
    (this as RequestConfig | undefined) ?? {};
  const scopedConfig = functionScopedConfig ?? {};

  const { 'content-type': _ct, ...headersWithoutContentType } = STATIC_HEADERS;
  const mergedHeaders: Record<string, string> = {
    ...(data instanceof FormData ? headersWithoutContentType : STATIC_HEADERS),
    ...(contextConfig.headers ?? {}),
    ...(scopedConfig.headers ?? {}),
  };

  const callerSignal = scopedConfig.signal ?? contextConfig.signal;
  const transforms =
    scopedConfig.transformResponse ??
    contextConfig.transformResponse ??
    defaultTransform;

  if (mockData !== undefined) {
    return new Promise<ApiResponse<T>>((resolve) => {
      setTimeout(() => {
        resolve({
          data: mockData.data,
          status: 200,
          statusText: 'OK',
          headers: mergedHeaders,
        });
      }, mockData.timeout);
    });
  }

  const { url, init } = buildRequest(method, uri, data, mergedHeaders);
  return executeWithRetry<T>(
    url,
    init,
    transforms,
    callerSignal,
    retryCount,
    retryDelay,
    0
  );
}

export function factory<Args extends any[], Return>(
  handler: (r: typeof request) => RequestFn<Args, Return>
) {
  return function (this: RequestContext, ...rest: Args) {
    const r = request.bind(this) as typeof request;
    return handler(r)(...rest);
  };
}
